import React, { useEffect } from 'react';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	FormLabel,
	Stack,
	Typography,
	Box,
	Divider,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { isValid } from 'date-fns';
import { useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import getCorrectFolderOwner from '@/hooks/getCorrectFolderOwner';
import { useGetDriveItemQuery } from '@/features/msGraph/msGraphApi';
import { getDriveItemFolderDisplayPath } from '@/features/msGraph/helper';
import { FolderSelectContextProvider } from '@/context/folderSelectContext';
import FolioCheckbox from '@/components/FolioCheckbox';

export const SaveEmailDialog = ({ open, handleClose, handleSave }) => {
	const formik = useFormikContext();
	const { values, setFieldValue } = formik;

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const { correctOwner: folderOwnerId } = getCorrectFolderOwner({
		deal: values.deal,
		client: values.client,
	});

	const { data: folderOwner } = useGetUserDetailsQuery(
		{ userId: folderOwnerId },
		{ skip: !folderOwnerId },
	);

	const itemId = values.deal?.driveId ?? values.client?.driveId ?? 'root';
	const driveId =
		values.deal?.clientFolderDriveId ??
		values.client?.clientFolderDriveId ??
		folderOwner?.clientFolderDriveId ??
		userDetails?.clientFolderDriveId;

	useEffect(() => {
		setFieldValue('driveId', driveId);
	}, [driveId, setFieldValue]);

	const { data: driveItem } = useGetDriveItemQuery(
		{
			driveId,
			itemId: values.selectedFolder,
		},
		{ skip: !values.selectedFolder || !driveId },
	);

	const hasSelection = values.selectedFolder && driveItem?.webUrl;

	const webUrl = () => {
		if (!hasSelection) return 'None';
		return getDriveItemFolderDisplayPath(driveItem, folderOwner, userDetails);
	};

	return (
		<Dialog
			open={open}
			onClose={(event, reason) => {
				if (reason && reason === 'backdropClick') return;
				handleClose();
			}}
			hideBackdrop={true}
			sx={{
				padding: '1em',
				'& .MuiDialog-paper': {
					width: '60%',
				},
			}}
		>
			<DialogTitle>Save email</DialogTitle>
			<DialogContent sx={{ paddingBottom: '2em' }}>
				<Stack spacing={2}>
					<Stack spacing={1}>
						<FormLabel>Selected folder</FormLabel>
						<Typography
							variant={hasSelection ? 'blue_text' : 'avatar_subtitle'}
							sx={{
								...(hasSelection && { fontWeight: 'bold' }),
							}}
						>
							{webUrl()}
						</Typography>
					</Stack>
					<FolderSelectContextProvider
						{...{
							selectedFolder: values.selectedFolder,
							setSelectedFolder: (folder) => setFieldValue('selectedFolder', folder),
							itemId,
							driveId,
							disableParent: !values.deal || itemId === 'root',
							cannotSelect: (folder) => {
								return folder.id === 'root';
							},
						}}
					/>
					<Divider />
					<Stack spacing={1} direction='row' alignItems='center'>
						<Box flex={1}>
							<FolioCheckbox
								checked={Boolean(values.createFollowUpTask)}
								onChange={(event) =>
									setFieldValue('createFollowUpTask', event.target.checked)
								}
								label='Create follow-up task'
							/>
						</Box>
						<LocalizationProvider dateAdapter={AdapterDateFns}>
							<DatePicker
								name='followUpDueDate'
								disablePast
								format='dd/MM/yyyy'
								value={values.followUpDueDate}
								sx={{ width: 200 }}
								onChange={(value) => {
									if (!isValid(value)) return;
									setFieldValue('followUpDueDate', value);
								}}
							/>
						</LocalizationProvider>
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color='primary'>
					Cancel
				</Button>
				<Button
					disabled={(values.selectedFolder ?? '').length === 0}
					variant='contained'
					color='primary'
					onClick={handleSave}
				>
					Send and save
				</Button>
			</DialogActions>
		</Dialog>
	);
};
